<template>
    <div class="dashboard hide-scrollbar" :class="[$mq]">
        <div class="boxes">
            <div
                v-for="(item, size, index) in widgetsConfig"
                :key="`${size}-${index}`"
                :class="{
                    directAccess: size === 'small',
                    'products-state small-widget': size === 'medium' || size === 'big',
                    'big-products' : size === 'big'
                }"
            >
                <template v-for="widget in item">
                    <DirectAccess v-if="size === 'small' && canShowWidget(widget)" :details="widget" />

                    <SmallWidget v-if="size === 'medium' && canShowWidget(widget)" :details="widget" />

                    <WidgetBig v-if="size === 'big' && canShowWidget(widget)" :details="widget" />
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import DirectAccess from '@/components/domain/dashboard/DirectAccess.vue'
import WidgetBig from '@/components/domain/dashboard/WidgetBig.vue'
import SmallWidget from '@/components/domain/dashboard/SmallWidget.vue'

const PENDING_PRODUCTION_REFRESH_MS = 2 * 60 * 1000

const PENDING_PRODUCTION_WIDGET = {
    id: 'pending_production',
    tool: 20,
    params: [
        { type: 'link', value: '/stock-pls' },
        { type: 'value', value: 'pending' },
        { type: 'store', value: 'production' },
        { type: 'color', value: '#BD1F1E' }
    ]
}

function normalizePendingProductionWidget(widget) {
    if (!widget || widget.id !== 'pending_production') {
        return widget
    }

    return { ...PENDING_PRODUCTION_WIDGET, ...widget, params: PENDING_PRODUCTION_WIDGET.params }
}

export default {
    name: 'HomeWidgets',
    components: {
        WidgetBig,
        DirectAccess,
        SmallWidget
    },
    data() {
        return {
            pendingProductionIntervalId: null
        }
    },
    computed: {
        locationConfig() {
            return this.$store.getters['login/getConfig'] || {}
        },
        enabledTools() {
            if (!Array.isArray(this.locationConfig.tools)) {
                return []
            }

            return this.locationConfig.tools.map((tool) => String(tool))
        },
        widgetsConfig() {
            const configWidget = this.locationConfig.configWidget || {}
            if (!this.enabledTools.includes('20')) {
                return configWidget
            }

            const medium = Array.isArray(configWidget.medium)
                ? configWidget.medium.map(normalizePendingProductionWidget)
                : []
            const hasPendingProductionWidget = medium.some((widget) => widget && widget.id === 'pending_production')

            if (hasPendingProductionWidget) {
                return { ...configWidget, medium }
            }

            return {
                ...configWidget,
                medium: [PENDING_PRODUCTION_WIDGET, ...medium]
            }
        },
        hasPendingProductionWidget() {
            const configWidget = this.widgetsConfig
            if (!configWidget || !configWidget.medium) {
                return false
            }

            return configWidget.medium.some((widget) => widget && widget.id === 'pending_production')
        },
        shouldRefreshPendingProduction() {
            return this.hasPendingProductionWidget && this.enabledTools.includes('20')
        },
        pendingProductionScopeKey() {
            const location = this.$store.getters['login/getLocation'] || {}
            const locationKey = location.id || location.token || ''
            return `${locationKey}:${this.shouldRefreshPendingProduction ? '1' : '0'}`
        }
    },
    watch: {
        pendingProductionScopeKey: {
            immediate: true,
            handler() {
                this.syncPendingProductionRefresh()
            }
        }
    },
    beforeDestroy() {
        this.stopPendingProductionRefresh()
    },
    methods: {
        syncPendingProductionRefresh() {
            this.stopPendingProductionRefresh()

            if (!this.shouldRefreshPendingProduction) {
                this.$store.dispatch('production/resetHomeWidgetData')
                return
            }

            this.$store.dispatch('production/loadPendingBatches', { reset: true })
            this.pendingProductionIntervalId = setInterval(() => {
                this.$store.dispatch('production/loadPendingBatches', {})
            }, PENDING_PRODUCTION_REFRESH_MS)
        },
        stopPendingProductionRefresh() {
            if (this.pendingProductionIntervalId !== null) {
                clearInterval(this.pendingProductionIntervalId)
                this.pendingProductionIntervalId = null
            }
        },
        getWidgetRequiredTools(widget) {
            const requiredTools = []
            const addTool = (tool) => {
                if (typeof tool === 'string' || typeof tool === 'number') {
                    requiredTools.push(String(tool))
                }
            }

            if (Array.isArray(widget && widget.tools)) {
                widget.tools.forEach(addTool)
            } else {
                addTool(widget && widget.tools)
            }

            addTool(widget && widget.tool)

            return [...new Set(requiredTools)]
        },
        canShowWidget(widget) {
            const requiredTools = this.getWidgetRequiredTools(widget)

            if (requiredTools.length === 0) {
                return true
            }

            return requiredTools.some((tool) => this.enabledTools.includes(tool))
        }
    }
}
</script>

<style lang="scss" scoped>
.zsiq_floatmain {
    display: none !important;
}

.dashboard {
    @include display-flex();
    @include align-items(center);
    width: 100%;
    overflow: auto;
    padding: 8px;
    height: 100%;

    .boxes {
        @include display-flex();
        @include justify-content(flex-start);
        @include align-content(flex-start);
        @include flex-wrap(nowrap);
        flex-direction: column;
        width: 100%;
        gap: 8px;
        height: 100%;
        flex-direction: column;

        .directAccess {
            @include display-flex();
            gap: 8px;
        }

        .card {
            @include display-flex();
            @include justify-content(center);
            @include align-items();
            @include border-radius(5px);
            @include interaction();
            flex-direction: column;
            background-color: #fff;
            width: 740px;
            margin: 20px;

            .title-card {
                padding: 15px 0px;
                background-color: #e2e2e2;
                position: relative;
                width: 100%;
                text-align: center;
                font-weight: 600;
            }

            .container {
                width: 100%;
                text-align: center;
                padding: 90px 8px;

                label {
                    text-align: center;
                    font-weight: 600;
                }
            }

            button {
                background-color: #1ba844;
                padding: 7px;
                border-radius: 6px;
                position: absolute;
                right: 14px;
                bottom: 8px;
            }
        }

        .products-state {
            display: flex;
            width: 100%;
            gap: 8px;
            flex-wrap: wrap;
            justify-content: space-between;

            .timer-container {
                width: calc(50% - 4px);
                min-width: 0px;
            }

        }

        .small-widget .box {
            height: 100px;
            flex: 1;
        }

        .big-products {
            flex: 1;
        }
    }
    &.portrait {
        height: unset;
        .boxes {
            gap: 8px;
            .box {
                .label {
                    @include font-size(s);
                    width: calc(100% - 80px);
                }
            }

            .directAccess {
                flex-direction: column;
                padding: 4px;
                gap: 16px;

                .box {
                    width: 100%;
                    margin: 0px;
                }
            }

            .small-widget {
                font-family: $title-bold;
                gap: 8px;
            }

            .products-state {
                flex-direction: column;
                padding: 4px;
                gap: 16px;
                justify-content: space-between;

                .box {
                    width: 100%;
                    margin: 0px;
                }

                .timer-container {
                    width: 100%;
                    margin: 0px;
                }
            }

            .count {
                font-size: 36px;
            }

            .details-products {
                gap: 20px;

                label span:first-child {
                    font-size: 61px;
                }

                label span:last-child {
                    font-size: 17px;
                }
            }
        }
    }
}
</style>
